"use client";

import { ToastContainer } from "react-toastify";
import { Toaster } from "sonner";
import { App as AntApp, ConfigProvider } from "antd";

import { BookingProvider } from "context/bookingContext";
import { RentProvider } from "context/rentContext";
import { NotificationProvider } from "context/notificationContext";
import WebInfoProvider from "context/WebInfoProvider";

import AOSWrapper from "components/Generals/AOSWrapper";
import ScrollToTop from "components/Generals/ScrollToTop";
import ScrollUpButton from "components/Generals/ScrollUpButton";
import AuthBoot from "components/Generals/AuthBoot";

/**
 * Application Context Providers wrapper
 * Groups all state management providers
 */
function AppContextProviders({ children, initialWebInfo, initialMenus }) {
  return (
    <NotificationProvider>
      <BookingProvider>
        <RentProvider>
          <WebInfoProvider
            initialWebInfo={initialWebInfo}
            initialMenus={initialMenus}
          >
            {children}
          </WebInfoProvider>
        </RentProvider>
      </BookingProvider>
    </NotificationProvider>
  );
}

/**
 * Global UI Components (Toasts, Notifications)
 * Rendered outside main content flow
 */
function GlobalUIComponents() {
  return (
    <>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
      <Toaster
        position="top-center"
        expand={true}
        closeButton
        duration={5000}
        style={{ zIndex: 99999 }}
        toastOptions={{
          style: {
            background: "white",
            color: "black",
            border: "1px solid #e5e7eb",
          },
          className: "sonner-toast",
          success: {
            style: {
              background: "#f0fdf4",
              color: "#166534",
              border: "1px solid #86efac",
            },
          },
          error: {
            style: {
              background: "#fef2f2",
              color: "#991b1b",
              border: "1px solid #fca5a5",
            },
          },
          warning: {
            style: {
              background: "#fffbeb",
              color: "#92400e",
              border: "1px solid #fde68a",
            },
          },
          info: {
            style: {
              background: "#eff6ff",
              color: "#1e40af",
              border: "1px solid #93c5fd",
            },
          },
        }}
      />
      <ScrollUpButton />
    </>
  );
}

/**
 * Root Providers Component
 * Wraps the entire application with necessary context and utilities
 */
export default function Providers({ children, initialWebInfo, initialMenus }) {
  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: "#0a2540",
          borderRadius: 8,
          fontFamily:
            '"TT Hoves", ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
        },
      }}
      modal={{
        styles: {
          mask: {
            backdropFilter: "blur(4px)",
          },
        },
      }}
    >
      <AntApp>
        {/* Scroll restoration on route change */}
        <ScrollToTop>
          {/* Authentication initialization */}
          <AuthBoot />

          {/* Application state providers */}
          <AppContextProviders
            initialWebInfo={initialWebInfo}
            initialMenus={initialMenus}
          >
            {/* Animation on scroll wrapper */}
            <AOSWrapper>
              {/* Page content - automatically wrapped with Suspense by loading.js */}
              {children}
            </AOSWrapper>
          </AppContextProviders>
        </ScrollToTop>

        {/* Global UI components (portals) */}
        <GlobalUIComponents />
      </AntApp>
    </ConfigProvider>
  );
}
